import { ButtonGroup } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import moment from "moment";

export default function CommonCalendar({
  pivot,
  handleThisMonth,
  handlePrevMonth,
  handleNextMonth,
  renderScheduleTypeSelector,
  scheduleMap,
  handleClickOpen,
  cellStyles,
}) {
  const now = pivot.clone();
  const today = moment().format("YYYY-MM-DD");
  const start = now.startOf("month");
  const end = moment(now).endOf("month");
  const calendar = [];

  [...Array(Number(start.format("d")))].map((_, i) =>
    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
    calendar.push(<Box sx={{ ...cellStyles }} key={`empty-start-${i}`} />),
  );

  while (start.unix() <= end.unix()) {
    const date = start.format("YYYY-MM-DD");
    calendar.push(
      <Box
        sx={{
          ...cellStyles,
          aspectRatio: "1 / 1",
          paddingLeft: "5px",
          paddingRight: "3px",
          overflow: "hidden",
          background: date === today ? "#ffefc9" : "#fff",
        }}
        key={date}
        onClick={() => handleClickOpen(date, null)}
      >
        {start.format("D")}
        {scheduleMap.has(date) &&
          scheduleMap.get(date).map(
            (s, i) =>
              !s.is_negative && (
                <Box
                  sx={{
                    paddingTop: "1px",
                    paddingLeft: "6px",
                    marginBottom: "4px",
                    height: "21px",
                    fontSize: "13px",
                    background: s.is_negative ? "#707070" : s.bg_color,
                    outlineWidth: s.is_settled ? 1 : 0,
                    outlineStyle: s.is_settled ? "solid" : "none",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                  key={`schedule-${s.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    s.source === 1 &&
                      !s.is_negative &&
                      handleClickOpen(date, s);
                  }}
                >
                  {!s.is_negative && (
                    <Typography
                      sx={{
                        display: "inline",
                        background: "#707070",
                        color: "white",
                        fontSize: "10px",
                        padding: "2px",
                        marginRight: "3px",
                        verticalAlign: "text-top",
                      }}
                    >
                      {s.start_time}
                    </Typography>
                  )}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "13px",
                      fontWeight: s.source === 1 ? "normal" : "bold",
                      color: s.is_negative
                        ? "white"
                        : (s.fg_color ?? "#763833"),
                    }}
                  >
                    {s.name + (s.is_negative ? "(不可)" : "")}
                  </Typography>
                </Box>
              ),
          )}
      </Box>,
    );
    start.add(1, "days");
  }

  [...Array((7 - (calendar.length % 7)) % 7)].map((_, i) =>
    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
    calendar.push(<Box sx={{ ...cellStyles }} key={`empty-end-${i}`} />),
  );

  return (
    <>
      <Box
        sx={{
          width: "100%",
          height: "40px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          alignSelf: "stretch",
          marginBottom: "24px",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: "Noto Sans JP",
            fontStyle: "normal",
            fontWeight: "400",
            fontSize: "24px",
            lineHeight: "133.4%",
            color: "rgba(0, 0, 0, 0.6)",
            marginLeft: "12px",
          }}
        >
          {pivot.format("YYYY年M月")}
        </Typography>
        <Stack
          direction="row"
          sx={{
            gap: "8px",
            width: "150px",
            height: "40px",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="medium"
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "8px 16px",
              width: "62px",
              height: "40px",
              background: "#21456A",
              boxShadow:
                "0px 1px 5px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)",
              borderRadius: "6px",
            }}
            onClick={handleThisMonth}
          >
            <Typography
              sx={{
                color: "var(--primary-contrast, #FFF)",
                fontFamily: "Noto Sans JP",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: "500",
                lineHeight: "24px",
                letterSpacing: "1.25px",
                textTransform: "uppercase",
              }}
            >
              今日
            </Typography>
          </Button>
          <ButtonGroup
            color="primary"
            orientation="horizontal"
            variant="contained"
            sx={{
              borderRadius: "var(--borderRadius, 6px)",
              background: "var(--primary-main, #21456A)",
              boxShadow:
                "0px 3px 1px -2px rgba(0, 0, 0, 0.20), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
            }}
          >
            <Button
              sx={{
                padding: "8px",
                width: "40px",
                height: "40px",
                borderRadius: "4px",
              }}
              onClick={handlePrevMonth}
            >
              <svg
                role="img"
                aria-label="Previous month"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M14.71 6.71047C14.32 6.32047 13.69 6.32047 13.3 6.71047L8.70998 11.3005C8.31998 11.6905 8.31998 12.3205 8.70998 12.7105L13.3 17.3005C13.69 17.6905 14.32 17.6905 14.71 17.3005C15.1 16.9105 15.1 16.2805 14.71 15.8905L10.83 12.0005L14.71 8.12047C15.1 7.73047 15.09 7.09047 14.71 6.71047Z"
                  fill="white"
                />
              </svg>
            </Button>
            <Button
              sx={{
                padding: "8px",
                width: "40px",
                height: "40px",
                borderRadius: "4px",
              }}
              onClick={handleNextMonth}
            >
              <svg
                role="img"
                aria-label="Next month"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M9.29006 6.70978C8.90006 7.09978 8.90006 7.72978 9.29006 8.11978L13.1701 11.9998L9.29006 15.8798C8.90006 16.2698 8.90006 16.8998 9.29006 17.2898C9.68006 17.6798 10.3101 17.6798 10.7001 17.2898L15.2901 12.6998C15.6801 12.3098 15.6801 11.6798 15.2901 11.2898L10.7001 6.69978C10.3201 6.31978 9.68006 6.31978 9.29006 6.70978Z"
                  fill="white"
                />
              </svg>
            </Button>
          </ButtonGroup>
        </Stack>
      </Box>

      {renderScheduleTypeSelector?.()}

      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridAutoFlow: "row",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0,
          borderRight: 1,
          borderBottom: 1,
          borderColor: "#999",
        }}
      >
        <Box sx={{ ...cellStyles, textAlign: "center", color: "#f00" }}>日</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>月</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>火</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>水</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>木</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>金</Box>
        <Box sx={{ ...cellStyles, textAlign: "center", color: "#00f" }}>土</Box>
        {calendar}
      </Box>
    </>
  );
}
